import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  COMMENT_BODY_MAX,
  COMMENT_BODY_MIN,
  memberTier,
  updateCommentSchema,
  type CommentDto,
  type UpdateCommentInput,
} from '@zoxo/shared';

import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import type { CommentTarget } from './comment-target';
import { CommentForm } from './comment-form';
import {
  useDeleteComment,
  useUpdateComment,
} from './use-comment-mutations';
import { describeSubmitError } from '../../lib/describe-submit-error';
import { useComments } from './use-comments';

/**
 * Comment section (US-3.1, AC-3) — YouTube-style 2-level threading.
 *
 * The API returns a FLAT array (created_at asc); here we group it into top-level
 * comments (parentId === null) and their replies (parentId === top-level id).
 * Depth is capped at 2 server-side, so replying to a reply still lands under the
 * same top-level parent (parentId = the reply's parentId), with a `@nickname`
 * prefill — never a 3rd indentation level.
 *
 * Suspense read (the detail route's Error Boundary covers failures). Author is
 * shown as nickname + company initials only (anonymity, AC-0.5/6).
 */
/**
 * 작성자 등급. 🫘 트롤러 → 🌱 우쭈쭈 → 🌸 공돈환장 → 🍎 신디케이트.
 *
 * 등급은 저장하지 않고 활동에서 계산한다 — 댓글이 지워지면 등급도 내려간다.
 */
function TierBadge({ comments }: { comments: number }) {
  const tier = memberTier({ comments });
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-surface-subtle px-1.5 text-[11px] text-text-subtle"
      title={`${tier.label} — ${tier.blurb}`}
    >
      <span aria-hidden>{tier.emoji}</span>
      {tier.label}
    </span>
  );
}

export function CommentList({ target }: { target: CommentTarget }) {
  const { data: comments } = useComments(target);

  // Group the flat list once per data change.
  const { topLevel, repliesByParent, activeCount } = useMemo(() => {
    const topLevel: CommentDto[] = [];
    const repliesByParent = new Map<string, CommentDto[]>();
    let activeCount = 0;

    for (const c of comments) {
      if (c.parentId === null) {
        topLevel.push(c);
      } else {
        const list = repliesByParent.get(c.parentId);
        if (list) list.push(c);
        else repliesByParent.set(c.parentId, [c]);
      }
      // Tombstones (deleted top-level placeholders) don't count toward the total.
      if (!c.deleted) activeCount += 1;
    }

    return { topLevel, repliesByParent, activeCount };
  }, [comments]);

  return (
    <section className="mt-4 border-t border-border pt-3" aria-label="댓글">
      {/* Total = all active comments (top-level + replies), matching YouTube. */}
      <h2 className="mb-2 mt-4 text-sm! font-bold text-text">댓글 {activeCount}</h2>

      <CommentForm target={target} />

      {topLevel.length === 0 ? (
        <p className="text-sm text-text-muted">아직 댓글이 없어요.</p>
      ) : (
        <ul className="flex list-none flex-col gap-2 p-0">
          {topLevel.map((c) => (
            <TopLevelComment
              key={c.id}
              target={target}
              comment={c}
              replies={repliesByParent.get(c.id) ?? []}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function TopLevelComment({
  target,
  comment,
  replies,
}: {
  target: CommentTarget;
  comment: CommentDto;
  replies: CommentDto[];
}) {
  const [replying, setReplying] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [prefill, setPrefill] = useState('');

  // replyCount is server-computed (active replies on this top-level comment).
  const replyCount = comment.replyCount ?? replies.length;

  // Opening the reply box from a reply prefills `@nickname `; from the top-level
  // comment it opens empty. Either way the reply targets THIS top-level id.
  const openReply = (mentionNickname?: string) => {
    setPrefill(mentionNickname ? `@${mentionNickname} ` : '');
    setReplying(true);
  };

  return (
    <li className="flex flex-col gap-1">
      {comment.deleted ? (
        <Tombstone />
      ) : (
        <CommentBody
          target={target}
          comment={comment}
          onReply={() => openReply()}
        />
      )}

      {replyCount > 0 && (
        <button
          type="button"
          className="inline-flex min-h-11 items-center self-start px-1 text-xs font-medium text-primary"
          aria-expanded={showReplies}
          onClick={() => setShowReplies((v) => !v)}
        >
          {showReplies ? `답글 숨기기` : `답글 ${replyCount}개 보기`}
        </button>
      )}

      {(showReplies || replying) && (
        <div className="ml-2 flex flex-col gap-2 border-l border-border pl-2">
          {showReplies &&
            replies.map((r) => (
              <CommentBody
                key={r.id}
                target={target}
                comment={r}
                isReply
                onReply={() => {
                  setShowReplies(true);
                  openReply(r.author.nickname);
                }}
              />
            ))}

          {replying && (
            <CommentForm
              target={target}
              parentId={comment.id}
              defaultBody={prefill}
              autoFocus
              onCancel={() => setReplying(false)}
              onSuccess={() => {
                setReplying(false);
                setShowReplies(true);
              }}
            />
          )}
        </div>
      )}
    </li>
  );
}

/** Soft-deleted top-level placeholder (kept because it still has active replies). */
function Tombstone() {
  return <p className="italic text-text-muted">삭제된 댓글이에요.</p>;
}

/**
 * Renders a comment body, highlighting a YouTube-style leading `@nickname` mention
 * as an accent chip. We only match the SINGLE leading `@\S+` token (the prefill we
 * generate when replying) and leave the rest as plain text — no in-text mentions.
 *
 * V1 has no profile route, so the chip is a visual accent only (no navigation link).
 */
function CommentBodyText({ body }: { body: string }) {
  const match = /^(@\S+)(\s*)([\s\S]*)$/.exec(body);
  if (!match) return <>{body}</>;
  const [, mention, gap, rest] = match;
  return (
    <>
      <span className="font-medium text-primary">{mention}</span>
      {gap}
      {rest}
    </>
  );
}

function CommentBody({
  target,
  comment,
  isReply = false,
  onReply,
}: {
  target: CommentTarget;
  comment: CommentDto;
  isReply?: boolean;
  onReply: () => void;
}) {
  const [editing, setEditing] = useState(false);

  // Double-guard: even though the backend blanks author on a tombstone, never
  // render author meta (nickname/initials) for a deleted comment (anonymity).
  if (comment.deleted) {
    return (
      <div>
        <Tombstone />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-1 flex flex-wrap gap-1 text-sm text-text-muted">
        <span>{comment.author.nickname}</span>
        {/*
          회사 초성 대신 등급을 보여준다. 초성은 실명 회사에 대한 익명 표기라
          지원사업 논의에서 아무 의미가 없고, PRD §2.4 에서 익명 정책을
          폐기했다. 대신 이 사람이 여기서 뭘 했는지를 보여준다.
        */}
        <TierBadge comments={comment.author.commentCount} />
      </div>

      {editing ? (
        <CommentEdit
          target={target}
          comment={comment}
          onDone={() => setEditing(false)}
        />
      ) : (
        <>
          <p className="m-0 break-words text-sm leading-relaxed text-text">
            <CommentBodyText body={comment.body} />
          </p>
          <div className="flex flex-wrap items-center gap-1">
            <Button type="button" variant="ghost" size="sm" onClick={onReply}>
              답글
            </Button>
            {comment.isMine && (
              <CommentOwnerActions
                target={target}
                commentId={comment.id}
                onEdit={() => setEditing(true)}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function CommentEdit({
  target,
  comment,
  onDone,
}: {
  target: CommentTarget;
  comment: CommentDto;
  onDone: () => void;
}) {
  const update = useUpdateComment(target, comment.id);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UpdateCommentInput>({
    resolver: zodResolver(updateCommentSchema),
    defaultValues: { body: comment.body },
  });

  const onSubmit = handleSubmit((values) =>
    update.mutate(values, { onSuccess: onDone }),
  );

  return (
    <form className="mt-1" onSubmit={(e) => void onSubmit(e)} noValidate>
      <div className="mb-2 flex flex-col gap-1">
        <label htmlFor={`comment-edit-${comment.id}`} className="sr-only">
          댓글 수정
        </label>
        <textarea
          id={`comment-edit-${comment.id}`}
          rows={3}
          maxLength={COMMENT_BODY_MAX}
          aria-invalid={errors.body ? 'true' : undefined}
          className="w-full resize-y rounded-lg border border-border-strong bg-surface px-3 py-2 text-sm leading-relaxed text-text outline-none transition-colors placeholder:text-text-subtle focus:border-primary focus:ring-2 focus:ring-primary/20 aria-invalid:border-danger"
          {...register('body')}
        />
        {errors.body && (
          <p className="text-xs text-danger" role="alert">
            댓글을 {COMMENT_BODY_MIN}~{COMMENT_BODY_MAX}자로 입력해 주세요.
          </p>
        )}
        {update.isError && (
          <p className="text-xs text-danger" role="alert">
            {describeSubmitError(update.error)}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1">
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          취소
        </Button>
        <Button type="submit" variant="secondary" size="sm" disabled={update.isPending}>
          {update.isPending ? '저장 중…' : '저장'}
        </Button>
      </div>
    </form>
  );
}

function CommentOwnerActions({
  target,
  commentId,
  onEdit,
}: {
  target: CommentTarget;
  commentId: string;
  onEdit: () => void;
}) {
  const del = useDeleteComment(target, commentId);
  const [confirming, setConfirming] = useState(false);

  return (
    <>
      <Button type="button" variant="ghost" size="sm" onClick={onEdit}>
        수정
      </Button>
      <Button
        type="button"
        variant="danger"
        size="sm"
        onClick={() => setConfirming(true)}
        disabled={del.isPending}
      >
        {del.isPending ? '삭제 중…' : '삭제'}
      </Button>
      {del.isError && (
        <p className="text-xs text-danger" role="alert">
          삭제에 실패했어요. 잠시 후 다시 시도해 주세요.
        </p>
      )}
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          del.mutate();
        }}
        title="정말로 댓글을 삭제할까요?"
        confirmLabel="예"
        cancelLabel="아니오"
        destructive
      />
    </>
  );
}
